import { ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { Download, Plus, RefreshCw, Send, Trash2, Upload } from 'lucide-react';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';

export interface PunchupButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'primary' | 'secondary' | 'destructive' | 'outline';
	/** Legacy text prop — prefer children for new callsites. */
	text?: string;
	children?: ReactNode;
	/** When true, disables the button and shows a spinner in place of the label. */
	isLoading?: boolean;
	/**
	 * Optional leading icon override.
	 * - When provided (any node, including `null`), it replaces the variant's default icon.
	 *   Pass `null` to render no leading icon on a `destructive` button.
	 * - When omitted, the `destructive` variant keeps its conventional `Trash2` icon and
	 *   every other variant renders no icon — preserving every existing call site.
	 * Use for danger actions that are not deletions (e.g. seats manual-mode activation).
	 */
	icon?: ReactNode;
}

const VARIANT_CLASS: Record<NonNullable<PunchupButtonProps['variant']>, string> = {
	primary: 'punchupButtonPrimary',
	secondary: 'punchupButtonSecondary',
	destructive: 'punchupButtonDestructive',
	outline: 'punchupButtonOutline',
};

/** Merge class-name parts, dropping any empty/falsy entry — the green-zone className idiom. */
const cx = (...parts: (string | undefined | false)[]): string => parts.filter(Boolean).join(' ');

export const PunchupButton: FC<PunchupButtonProps> = ({
	variant = 'primary',
	text,
	children,
	isLoading,
	icon,
	disabled,
	className = '',
	...props
}) => {
	const variantClass = VARIANT_CLASS[variant];
	// Two sequential decisions: (1) an explicit `icon` (including `null`) always wins,
	// (2) otherwise the destructive variant keeps its conventional Trash2 default and
	// every other variant renders no leading icon.
	let leadingIcon: ReactNode;
	if (icon !== undefined) {
		leadingIcon = icon;
	} else {
		leadingIcon = variant === 'destructive' ? <Trash2 size={16} aria-hidden /> : null;
	}
	return (
		<button
			className={cx('punchupButton', variantClass, className)}
			disabled={disabled || isLoading}
			aria-disabled={disabled || isLoading ? true : undefined}
			{...props}
		>
			{isLoading ? (
				<PunchupSpinner size={16} />
			) : (
				<>
					{leadingIcon}
					{children ?? text}
				</>
			)}
		</button>
	);
};

/**
 * Add button — orange outline with a leading Plus icon.
 *
 * Use this wherever an action opens a create form or adds a new entity.
 * The Plus icon is always rendered automatically — callers must NOT add it manually.
 *
 * Downstroke equivalent: `PunchupAddButton` from `@punchup/ui-common/components/common/PunchupAddButton`.
 */
export const PunchupAddButton: FC<Omit<PunchupButtonProps, 'variant'>> = (props) => (
	<PunchupButton variant="outline" {...props}>
		<Plus size={16} aria-hidden />
		{props.children ?? props.text}
	</PunchupButton>
);

interface IconLabelButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
	/**
	 * Required as a string so the wrapper can use it as both the `aria-label`
	 * (accessible name for icon-only mode) and the `title` attribute (hover
	 * tooltip). Either may be overridden via an explicit `aria-label` / `title`
	 * prop. Whether the visible label ever appears as text is governed by the
	 * wrapper's modifier class (`punchupButtonIconOnly` hides it at every
	 * viewport; `punchupButtonIconCollapse` hides it only below 768px).
	 */
	children: string;
	/**
	 * When true, the 16px `PunchupSpinner` replaces the leading icon and the
	 * button is disabled — the in-progress signal for a refresh / download /
	 * upload / send. Forwarded to the base `PunchupButton.isLoading`.
	 */
	isLoading?: boolean;
}

/**
 * Resolve the accessible-name attributes for an icon-label button: both
 * `aria-label` and `title` default to the `children` string and may be
 * overridden independently by the caller. Centralising this keeps the
 * override-vs-default contract identical across all four icon-label wrappers.
 */
const resolveAccessibleAttrs = ({
	children,
	'aria-label': ariaLabel,
	title,
}: {
	children: string;
	'aria-label'?: string;
	title?: string;
}): { 'aria-label': string; title: string } => ({
	'aria-label': ariaLabel ?? children,
	title: title ?? children,
});

interface IconLabelButtonInternalProps extends IconLabelButtonProps {
	/** Leading icon node — rendered before the (CSS-collapsible) label. */
	icon: ReactNode;
	/** Base visual style passed through to `PunchupButton`. */
	variant: NonNullable<PunchupButtonProps['variant']>;
	/** Modifier class controlling the icon-only / icon-collapse behaviour. */
	modifierClass: 'punchupButtonIconOnly' | 'punchupButtonIconCollapse';
}

/**
 * Shared body for the four icon-label wrappers (Refresh, Download, Upload,
 * Send). They differ only by icon, base variant, and modifier class — this
 * helper guarantees identical prop handling (accessible-name resolution,
 * className merge, label span) across all four.
 */
const IconLabelButton: FC<IconLabelButtonInternalProps> = ({
	icon,
	variant,
	modifierClass,
	children,
	className = '',
	title,
	'aria-label': ariaLabel,
	...props
}) => (
	<PunchupButton
		variant={variant}
		{...resolveAccessibleAttrs({ children, 'aria-label': ariaLabel, title })}
		className={cx(modifierClass, className)}
		{...props}
	>
		{icon}
		<span className="punchupButtonLabel">{children}</span>
	</PunchupButton>
);

/**
 * Refresh button — quiet secondary icon-only square with a `RefreshCw` icon.
 *
 * Re-fetch / re-validate data (analytics, table page headers, etc.). Reserved
 * for utility actions that should not compete visually with primary CTAs in the
 * same row. The `RefreshCw` icon is always rendered automatically — callers
 * must NOT add it manually. Pass `disabled` while data is loading to prevent
 * overlapping requests.
 *
 * The `children` string doubles as the button's `aria-label` (accessible name)
 * and `title` attribute (hover tooltip); either may be overridden. See
 * `Punchup/CLAUDE.md → Buttons` for the utility-vs-navigation taxonomy that
 * puts Refresh in the secondary tier.
 */
export const PunchupRefreshButton: FC<IconLabelButtonProps> = (props) => (
	<IconLabelButton
		icon={<RefreshCw size={16} aria-hidden />}
		variant="secondary"
		modifierClass="punchupButtonIconOnly"
		{...props}
	/>
);

/**
 * Download button — quiet secondary icon-only square with a `Download` icon.
 *
 * Use for any "download CSV / XLSX / image" action. The button is always
 * rendered as a 40x40 icon-only square — the label never appears as text;
 * `children` is pinned as both the `aria-label` (accessible name) and the
 * `title` attribute (hover tooltip), either of which the caller may override.
 * See `Punchup/CLAUDE.md → Buttons` for the utility-vs-navigation taxonomy that
 * puts Download in the secondary tier.
 *
 * Do not write a custom outline button with a manual `<Download>` icon.
 */
export const PunchupDownloadButton: FC<IconLabelButtonProps> = (props) => (
	<IconLabelButton
		icon={<Download size={16} aria-hidden />}
		variant="secondary"
		modifierClass="punchupButtonIconOnly"
		{...props}
	/>
);

/**
 * Upload button — orange outline with a leading `Upload` icon.
 *
 * Mirrors `PunchupDownloadButton`'s mobile-collapse behaviour: below 768px the
 * label hides and the button becomes an icon-only square. Use this for any
 * upload-CSV-style action.
 */
export const PunchupUploadButton: FC<IconLabelButtonProps> = (props) => (
	<IconLabelButton
		icon={<Upload size={16} aria-hidden />}
		variant="outline"
		modifierClass="punchupButtonIconCollapse"
		{...props}
	/>
);

/**
 * Send button — orange outline with a leading `Send` icon.
 *
 * Mirrors `PunchupDownloadButton` / `PunchupUploadButton`: collapses to an
 * icon-only square below 768px so the title-row action group stays compact on
 * mobile. Use this for any "navigate to send tickets / messages / etc." action
 * that originates from the table-page title row.
 */
export const PunchupSendButton: FC<IconLabelButtonProps> = (props) => (
	<IconLabelButton
		icon={<Send size={16} aria-hidden />}
		variant="outline"
		modifierClass="punchupButtonIconCollapse"
		{...props}
	/>
);
